import { ShieldX } from 'lucide-react';
import type { ReactNode } from 'react';

import { useSession } from './sessionStore';

/**
 * Garde d'acces de l'administration de plateforme.
 *
 * REFUS PAR DEFAUT (§3), et litteralement : le composant ne rend ses enfants que dans
 * l'unique cas `estAdministrateur === true`. Tout le reste — `null`, `false`, un etat
 * inattendu — tombe sur un refus. La logique n'est pas « bloquer si non admin » mais
 * « laisser passer si et seulement si admin », ce qui n'a pas les memes trous.
 *
 * <para>
 * CE N'EST PAS LA SECURITE, C'EST L'AFFICHAGE. La securite reelle est cote API : chaque
 * route `/v1/admin/*` exige la politique `AdministrateurPlateforme`, et un navigateur
 * trafique qui forcerait ce composant a rendre ses enfants n'obtiendrait que des 403.
 * Cette garde evite d'afficher une coquille vide et de bombarder l'API d'appels refuses.
 * </para>
 *
 * <para>
 * Elle enveloppe le ROUTEUR ENTIER, pas chaque route : il n'existe aucune route publique
 * dans cette application (§1), donc aucune raison de repeter la garde et aucune occasion
 * de l'oublier en ajoutant un ecran.
 * </para>
 */
export function GardeAdmin({
  children,
  formulaireConnexion,
}: {
  children: ReactNode;
  formulaireConnexion: ReactNode;
}) {
  const estAdministrateur = useSession((etat) => etat.estAdministrateur);

  if (estAdministrateur === true) {
    return <>{children}</>;
  }

  if (estAdministrateur === false) {
    return <AccesRefuse />;
  }

  // `null` : session non etablie. On demande a s'identifier.
  return <>{formulaireConnexion}</>;
}

/**
 * Ecran de refus.
 *
 * Volontairement avare en informations : il ne dit pas si le compte existe, ni quel role
 * lui manque, ni ou se trouve l'application utilisateur. Quelqu'un qui atteint cet ecran
 * sans y avoir droit ne doit rien apprendre de plus qu'en ne l'atteignant pas.
 */
function AccesRefuse() {
  const deconnecter = useSession((etat) => etat.deconnecter);

  return (
    <div className="flex min-h-dvh items-center justify-center bg-fond px-4">
      <div
        role="alert"
        className="w-full max-w-sm space-y-4 rounded-[--radius-astro] border border-danger/40 bg-surface p-6 text-center"
      >
        <div className="mx-auto flex size-10 items-center justify-center rounded-full bg-danger-voile">
          <ShieldX className="size-5 text-danger" aria-hidden />
        </div>

        <div className="space-y-1">
          <h1 className="text-base font-semibold text-texte">Acces refuse</h1>
          <p className="text-texte-doux">
            Ce compte n’a pas acces a l’administration de la plateforme.
          </p>
        </div>

        <button
          type="button"
          onClick={() => void deconnecter()}
          className="w-full rounded-[--radius-astro] border border-bordure bg-surface-haute px-3 py-2 font-medium text-texte transition-colors duration-150 hover:bg-surface-survol"
        >
          Revenir a la connexion
        </button>
      </div>
    </div>
  );
}
